import { motion } from 'framer-motion'
import { ArrowRightIcon, CheckIcon, CodeBracketIcon, DevicePhoneMobileIcon, GlobeAltIcon, RocketLaunchIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { CodeIcon } from '@/components/icons/ServiceIcons'
import styles from './DesarrolloWeb.module.css'

export default function DesarrolloWeb() {
  const features = [
    {
      icon: CodeBracketIcon,
      title: "Desarrollo Frontend",
      description: "Interfaces modernas con React, Next.js y tecnologías de vanguardia"
    },
    {
      icon: GlobeAltIcon,
      title: "Backend Robusto",
      description: "APIs escalables con Node.js, Python y arquitecturas en la nube"
    },
    {
      icon: DevicePhoneMobileIcon,
      title: "Responsive Design",
      description: "Experiencias perfectas en todos los dispositivos y pantallas"
    },
    {
      icon: RocketLaunchIcon,
      title: "Optimización Performance",
      description: "Aplicaciones rápidas optimizadas para SEO y conversión"
    }
  ]

  const technologies = [
    { name: "React", category: "Frontend" },
    { name: "Next.js", category: "Framework" },
    { name: "TypeScript", category: "Language" },
    { name: "Node.js", category: "Backend" },
    { name: "Python", category: "Backend" },
    { name: "PostgreSQL", category: "Database" },
    { name: "MongoDB", category: "Database" },
    { name: "AWS", category: "Cloud" },
    { name: "Docker", category: "DevOps" },
    { name: "Kubernetes", category: "DevOps" },
    { name: "GraphQL", category: "API" },
    { name: "REST API", category: "API" }
  ]

  const process = [
    {
      step: "01",
      title: "Análisis & Planificación",
      description: "Definimos objetivos, audiencia y arquitectura técnica óptima"
    },
    {
      step: "02", 
      title: "Diseño UX/UI",
      description: "Creamos wireframes, prototipos y diseños centrados en el usuario"
    },
    {
      step: "03",
      title: "Desarrollo",
      description: "Codificación con metodologías ágiles y mejores prácticas"
    },
    {
      step: "04",
      title: "Testing & Deploy",
      description: "Pruebas exhaustivas y despliegue en producción"
    }
  ]

  const benefits = [
    "Tiempo de carga inferior a 3 segundos",
    "Diseño responsive y mobile-first",
    "SEO optimizado desde el desarrollo",
    "Escalabilidad para millones de usuarios",
    "Integración con sistemas existentes",
    "Mantenimiento y soporte continuo"
  ]

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        {/* Background Elements */}
        <div className={styles.backgroundElements}>
          <div className={styles.backgroundBlur1} />
          <div className={styles.backgroundBlur2} />
        </div>

        {/* Code Animation */}
        <div className={styles.codeAnimation}>
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className={styles.codeElement}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0, 1, 0],
                y: [-20, 0, 20]
              }}
              transition={{
                duration: 4 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3
              }}
            >
              {['<div>', '</div>', 'function()', 'const', 'return', 'import'][Math.floor(Math.random() * 6)]}
            </motion.div>
          ))}
        </div>

        <div className={styles.heroContent}>
          <div className={styles.heroGrid}>
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className={styles.badge}
              >
                Desarrollo Web
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={styles.heroTitle}
              >
                Aplicaciones web{' '}
                <span className={styles.titleGradient}>
                  excepcionales
                </span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className={styles.heroDescription}
              >
                Creamos aplicaciones web modernas, rápidas y escalables que destacan por su 
                rendimiento, diseño y experiencia de usuario excepcional.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className={styles.heroButtons}
              >
                <Link to="/contacto">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={styles.primaryButton}
                  >
                    Iniciar Proyecto
                    <ArrowRightIcon className={styles.buttonIcon} />
                  </motion.button>
                </Link>
                <Link to="/casos">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={styles.secondaryButton}
                  >
                    Ver Portfolio
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={styles.visualContainer}
            >
              <div className={styles.codeVisualization}>
                <motion.div
                  animate={{
                    rotate: [0, 360]
                  }}
                  transition={{
                    duration: 25,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  className={styles.orbitRing}
                >
                  {/* Code Icon Center */}
                  <div className={styles.centerIcon}>
                    <CodeBracketIcon className={styles.centerIconSvg} />
                  </div>
                  
                  {/* Orbiting Tech Icons */}
                  {[0, 60, 120, 180, 240, 300].map((rotation, index) => {
                    const techs = ['React', 'Node', 'TS', 'API', 'DB', 'Cloud']
                    return (
                      <motion.div
                        key={index}
                        className={styles.orbitItem}
                        style={{
                          transformOrigin: '0 140px',
                        }}
                        animate={{
                          rotate: rotation
                        }}
                        transition={{
                          duration: 15,
                          repeat: Infinity,
                          ease: "linear"
                        }}
                      >
                        <motion.div
                          className={styles.techIcon}
                          animate={{
                            rotate: -rotation
                          }}
                          transition={{
                            duration: 15,
                            repeat: Infinity,
                            ease: "linear"
                          }}
                        >
                          {techs[index]}
                        </motion.div>
                      </motion.div>
                    )
                  })}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.sectionHeader}
          >
            <h2 className={styles.sectionTitle}>
              Servicios de Desarrollo
            </h2>
            <p className={styles.sectionDescription}>
              Ofrecemos desarrollo full-stack con las tecnologías más avanzadas 
              del mercado para crear soluciones web robustas y escalables
            </p>
          </motion.div>

          <div className={styles.featuresGrid}>
            {features.map((feature, index) => {
              const IconComponent = feature.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                  className={styles.featureCard}
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className={styles.featureIcon}
                  >
                    <IconComponent className={styles.featureIconSvg} />
                  </motion.div>
                  <h3 className={styles.featureTitle}>
                    {feature.title}
                  </h3>
                  <p className={styles.featureDescription}>
                    {feature.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className={styles.sectionAlternate}>
        <div className={styles.sectionContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.sectionHeader}
          >
            <h2 className={styles.sectionTitle}>
              Stack Tecnológico
            </h2>
            <p className={styles.sectionDescription}>
              Utilizamos las tecnologías más modernas y probadas para garantizar 
              el máximo rendimiento y escalabilidad
            </p>
          </motion.div>

          <div className={styles.techGrid}>
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -5 }}
                className={styles.techCard}
              >
                <h4 className={styles.techName}>
                  {tech.name}
                </h4>
                <span className={styles.techCategory}>
                  {tech.category}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.sectionHeader}
          >
            <h2 className={styles.sectionTitle}>
              Nuestro Proceso
            </h2>
            <p className={styles.sectionDescription}>
              Un enfoque estructurado que garantiza resultados excepcionales 
              en cada proyecto de desarrollo web
            </p>
          </motion.div>

          <div className={styles.processGrid}>
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={styles.processWrapper}
              >
                <div className={styles.processCard}>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className={styles.processNumber}
                  >
                    {step.step}
                  </motion.div>
                  <h3 className={styles.processTitle}>
                    {step.title}
                  </h3>
                  <p className={styles.processDescription}>
                    {step.description}
                  </p>
                </div>

                {/* Connecting Line */}
                {index < process.length - 1 && (
                  <div className={styles.processLine} />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.sectionAlternate}>
        <div className={styles.sectionContainer}>
          <div className={styles.benefitsGrid}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className={styles.sectionTitle}>
                ¿Por qué elegirnos?
              </h2>
              <p className={styles.benefitsDescription}>
                Entregamos aplicaciones web que no solo funcionan perfectamente, 
                sino que impulsan el crecimiento de tu negocio
              </p>
              
              <div className={styles.benefitsList}>
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={styles.benefitItem}
                  >
                    <CheckIcon className={styles.benefitIcon} />
                    <span className={styles.benefitText}>
                      {benefit}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className={styles.benefitsVisual}
            >
              <div className={styles.benefitsCard}>
                {/* Background Pattern */}
                <div className={styles.benefitsPattern} />
                
                <div className={styles.benefitsContent}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className={styles.benefitsIcon}
                  >
                    <CodeBracketIcon className={styles.benefitsIconSvg} />
                  </motion.div>
                  
                  <h3 className={styles.benefitsTitle}>
                    Código de Calidad
                  </h3>
                  <p className={styles.benefitsText}>
                    Escribimos código limpio, mantenible y siguiendo las mejores 
                    prácticas de la industria para garantizar la longevidad de tu proyecto.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.ctaCard}
          >
            <h2 className={styles.ctaTitle}>
              ¿Listo para tu nueva web?
            </h2>
            <p className={styles.ctaDescription}>
              Transformemos tu idea en una aplicación web excepcional que impulse tu negocio
            </p>
            
            <div className={styles.ctaButtons}>
              <Link to="/contacto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={styles.primaryButton}
                >
                  Iniciar Proyecto
                  <ArrowRightIcon className={styles.buttonIcon} />
                </motion.button>
              </Link>
              <Link to="/casos">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={styles.secondaryButton}
                >
                  Ver Portfolio
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
